import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import ProButton from "../../../Components/ProButton";
import ProToggleField from "../../../Components/Inputs/ToggleButton";
import ProTextInput from "../../../Components/Inputs/TextField";
import ProSelectField from "../../../Components/Inputs/SelectField";
import TabGridAndFormLayout from "../../../Components/ProForms/ProFormsLayout/TabGridAndFormLayout";
import ProGrid, { getSelectFilterOperator } from "../../../Components/ProGrid/v2";
import NameCell from "../../../Components/ProGrid/components/NameCell";
import DeleteCell from "../../../Components/ProGrid/components/DeleteCell";
import DateField from "../../../Components/Inputs/DateField";
import { Box, Tooltip } from "@mui/material";
import HelpIcon from "@mui/icons-material/Help";
import { STAUTES } from "../../../../utils/constant";
import usePageState from "../../../../utils/customHooks/usePageState";
import { errorToast, successToast } from "../../../../utils/toastHelper";
import {
  prepareDefaultValues,
  prepareInitialConfig,
  convertResponseDate,
  fieldTruePermission,
  checkEditEnabled,
  changeMode,
  changeModeForField,
  formatPayloadDate,
  convertTimeToResponse,
  convertResponseDateTime
} from "../../../../utils/formHelper";
import {
  GET_ALL_PROJECTEMPLOYEE,
  ADD_PROJECT_PROJECTEMPLOYEE,
  UPDATE_PROJECT_PROJECTEMPLOYEE,
  GET_ALL_PROJECTJOBTITLES,
  GET_ALL_MENTOR,
  GET_NonMappedEmployee,
  GET_PROJECT_JOBTITLE,
  GET_ALL_EMPLOYEE_TERM,
  GET_PROJECTEMPLOYEE,
  UPLOAD_PROJECT_EMPLOYEES,
  GET_PROJECT_JOB
} from "../../../../utils/services/apiPath";
import { postApi, getApi } from "../../../../utils/services";
import { R_PROJECTS, R_PROJECTS_EMPLOYEE } from "../../../../utils/permissionReferenceConfig";
import { subModPermissionSelector } from "../../../store/features/permissions/permissionsSlice";
import usePermission from "../../../../utils/customHooks/usePermission";
import { defaultDropdownConfig, weekdaysOptions } from "../../../../utils/dropdownHelper";
import useColumn from "../../../../utils/customHooks/useColumns";
import { ReactSpreadsheetImport } from "react-spreadsheet-import";
import ApprenticeMentorHistory from "./ApprenticeMentorHistory";
import FormSectionWrapper from "../../../Components/ProForms/FormSectionWrapper";
import DialogModal from "../../../Components/DialogModel";
import ProCheckbox from "../../../Components/Inputs/CheckboxField";
import { EmployeesExcelData, mapProjectEmployee, projectEmployeeFormConfing } from "../projectData";
import CheckBoxGroup from "../../../Components/Inputs/CheckBoxGroup";
import TimeField from "../../../Components/Inputs/TimeField";
import { convertToCSV, downloadCSV } from "../../../../utils/csvExporter";
import { Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button } from "@mui/material";

const fields = EmployeesExcelData?.map(fieldName => ({
  label: fieldName,
  key: fieldName,
  fieldType: { type: "input" }
}));

const ToggleCell = ({ params, toggleStates, employeeListPermission, handleStatusToggle }) => {
  const currentToggleState = toggleStates.hasOwnProperty(params.row.employmentTermId)
    ? toggleStates[params.row.employmentTermId]
    : params.row.isActive;

  const toggleForm = useForm({
    defaultValues: { isActive: currentToggleState }
  });

  useEffect(() => {
    toggleForm.setValue("isActive", currentToggleState);
  }, [currentToggleState, toggleForm]);

  return (
    <ProToggleField
      name="isActive"
      control={toggleForm.control}
      label=""
      rules={{}}
      mode="edit"
      permission={{
        canView: true,
        canEdit: employeeListPermission.canEdit
      }}
      onChange={(event, newValue) => {
        toggleForm.setValue("isActive", currentToggleState);
        handleStatusToggle(params, newValue);
      }}
      size="small"
    />
  );
};

export default function ProjectEmployees() {
  let { id } = useParams();
  const [isOpen, setIsOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [formConfig, setFormConfig] = useState(prepareInitialConfig(projectEmployeeFormConfing));

  const { handleSubmit, control, setValue, watch } = useForm({
    defaultValues: prepareDefaultValues(projectEmployeeFormConfing)
  });
  const {
    currentPage,
    pageSize,
    data: employeeData,
    rowCount,
    status,
    sortModel,
    searchString,
    setPageState,
    setPagination,
    setStatus,
    setSortModel,
    setSearchString
  } = usePageState();
  const { "Add Employee": addEmployeePermission, "Employees List": employeeListPermission } = usePermission(
    subModPermissionSelector(R_PROJECTS, R_PROJECTS_EMPLOYEE),
    ["Add Employee", "Employees List"]
  );
  const [showLogGrid, setShowLogGrid] = useState(false);
  const [showGrid, setShowGrid] = useState(true);
  const [projectEmployeeData, setProjectEmployeeData] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [mentors, setMentors] = useState([]);
  const [jobTitle, setJobTitle] = useState([]);
  const [employeeTermType, setEmployeeTermType] = useState([]);
  const [employeeTermId, setEmployeeTermId] = useState("");
  const [showProjectEmployeesConfirmModal, setShowProjectEmployeesConfirmModal] = useState(false);
  const [apiResponse, setApiResponse] = useState("");

  const [toggleStates, setToggleStates] = useState({});
  const [pendingToggle, setPendingToggle] = useState(null);

  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    params: null,
    newStatus: null,
    employeeName: "",
    statusText: ""
  });

  const fringe = watch("fringe");
  const jobTitleWatch = watch("projectJobTitleId");
  const hourlyWageRate = watch("rate");
  const projectJobTitleId = watch("projectJobTitleId");
  const isApprentice = watch("isApprentice") ?? false;
  const isMentor = watch("isMentor") ?? false;
  const startDate = watch("startDate");
  const [isAddMode, setIsAddMode] = useState(false);
  const [projectJobData, setProjectJobData] = useState({});

  const isEditModeOn = useMemo(() => checkEditEnabled(false, formConfig), [formConfig]);

  const onEdit = () => setFormConfig(changeMode("edit"));
  const handleReadValueClick = name => setFormConfig(changeModeForField(name, "edit"));

  const resetForm = () => {
    setProjectEmployeeData({});
    setShowGrid(true);
    setFormConfig(changeMode("read"));
  };

  const onCancel = () => {
    if (projectEmployeeData?.employmentTermId) setFormConfig(changeMode("read"));
    else resetForm();
  };

  const onProjectEmployeeClick = params => {
    setShowGrid(false);
    getProjectEmployeeData(params.row.employmentTermId);
    setShowLogGrid(true);
  };

  const addButtonAction = () => {
    Object.keys(formConfig).forEach(field => setValue(field, null));
    onEdit();
    setProjectEmployeeData({});
    setShowGrid(false);
    setShowLogGrid(false);
    setValue("isApprentice", false);
    setValue("isMentor", false);
    getAllEmployees(false);
    setEmployeeTermId("");
    getMentors("");
    setIsAddMode(true);
  };

  const getProjectJobData = async projectJobTitleId => {
    if (projectJobTitleId) {
      const response = await getApi(GET_PROJECT_JOB(id, projectJobTitleId));
      if (response?.error) return errorToast("Unable to load job details. Please try again.");
      const projectJob = response;
      if (projectJob) {
        const data = {
          IsRole: projectJob.isAdministrativeRole
        };
        setProjectJobData(data);
      }
    }
  };

  useEffect(() => {
    if (jobTitleWatch) getProjectJobData(jobTitleWatch);
  }, [jobTitleWatch]);

  const onSave = async data => {
    const {
      startDate,
      endDate,
      isApprentice,
      isMentor,
      isActive,
      fringe,
      rate,
      typicalshiftdays,
      shiftEndTime,
      shiftStartTime,
      actualHourlyRate,
      ...rest
    } = data;

    setIsSaving(true);
    const employeeId = projectEmployeeData?.employmentTermId || null;
    const payload = {
      ...rest,
      isApprentice: !!isApprentice,
      isMentor: !!isMentor,
      projectId: id,
      mentorId: null,
      typicalShiftDay: typicalshiftdays?.length > 0 ? Array.from(new Set(typicalshiftdays))?.toString() : "",
      shiftStartTime: convertTimeToResponse(shiftStartTime),
      shiftEndTime: convertTimeToResponse(shiftEndTime),
      isActive: !!isActive,
      fringe: parseFloat(fringe),
      rate: parseFloat(rate),
      actualHourlyRate: parseFloat(actualHourlyRate),
      employmentTermId: employeeId,
      startDate: formatPayloadDate(startDate),
      endDate: formatPayloadDate(endDate)
    };
    const { error, data: employeeInfo } = await postApi(employeeId ? UPDATE_PROJECT_PROJECTEMPLOYEE : ADD_PROJECT_PROJECTEMPLOYEE, payload);
    setIsSaving(false);
    if (error) return errorToast(error);
    getAllProjectEmployee();
    setFormConfig(changeMode("read"));
    successToast(`Employee ${employeeId ? "Updated" : "Added"} Successfully`);
    const eId = employeeId || employeeInfo?.guidId;
    if (eId) getProjectEmployeeData(eId);
    else resetForm();
  };

  const splitAndFilter = str => str?.split(",").filter(v => v) || [];

  const getProjectEmployeeData = async employmentTermId => {
    const response = await getApi(`${GET_PROJECTEMPLOYEE}/${id}/employees/${employmentTermId}`);
    if (response?.error) return errorToast(response.error);
    const data = response?.data ?? response;
    if (!data) return errorToast("Failed to load employee data");
    const obj = {
      ...data,
      typicalshiftdays: splitAndFilter(data?.typicalShiftDay),
      shiftEndTime: convertResponseDateTime(data?.shiftEndTime),
      shiftStartTime: convertResponseDateTime(data?.shiftStartTime),
      startDate: convertResponseDate(data.startDate),
      endDate: convertResponseDate(data.endDate),
      actualHourlyRate: data.actualHourlyTotalComp,
      isApprentice: data.isApprentice,
      isMentor: data.isMentor,
      mentorId: data?.mentorTypeDropDown?.value,
      hasTimesheets: data.hasTimesheets
    };
    setProjectEmployeeData(obj);
    const newObject = { label: `${data.firstName} ${data.lastName}-${data.internalEmployeeId}`, value: data.employeeId };
    getAllEmployees(data.isApprentice, newObject);
    getProjectEmployeeLog(data.employmentTermId);
    if (data.isApprentice) {
      getMentors(data.employmentTermId, data.mentorTypeDropDown);
    }
    Object.keys(formConfig).forEach(field => setValue(field, obj[field]));
  };

  const getAllProjectEmployee = useCallback(async () => {
    setStatus(STAUTES.LOADING);
    const payload = {
      pageIndex: searchString ? 1 : currentPage + 1,
      pageSize: pageSize,
      searchString: searchString,
      orderByAscending: true,
      orderCol: "fullName",
      ...sortModel
    };
    const { data, totalRecords, error } = await postApi(GET_ALL_PROJECTEMPLOYEE, payload, { params: { projectId: id } });
    setStatus(STAUTES.IDLE);
    if (error) return errorToast(error);
    setPageState(prevPageInfo => ({ ...prevPageInfo, data: data || [], rowCount: totalRecords }));
    return data;
  }, [currentPage, pageSize, sortModel, searchString, setPageState, setStatus]);

  const getProjectEmployeeLog = async employeeTermId => {
    setEmployeeTermId(employeeTermId);
  };

  const getAllJobTitles = async () => {
    const { data } = await postApi(`${GET_ALL_PROJECTJOBTITLES}?projectId=${id}`, defaultDropdownConfig);
    const jobTitles = Array.isArray(data)
      ? data.map(({ isExternalJobTitle, externalJobTitle, jobTitle, projectJobTitleId }) => ({
          label: isExternalJobTitle === true ? externalJobTitle : jobTitle,
          value: projectJobTitleId
        }))
      : [];
    setJobTitle(jobTitles.filter(item => typeof item.label === "string" && typeof item.value === "string"));
  };

  const getAllEmployeeTermType = async () => {
    const { data } = await postApi(GET_ALL_EMPLOYEE_TERM, defaultDropdownConfig);
    setEmployeeTermType(data?.map(item => ({ label: item.employmentTermType, value: item.employmentTermTypeId })) || []);
  };

  const getAllEmployees = async (isApprentice, preSelectedEmployee) => {
    if (preSelectedEmployee) return setEmployees([preSelectedEmployee]);
    const response = await getApi(`${GET_NonMappedEmployee(id)}?isApprentice=${!!isApprentice}`);
    if (response?.error) {
      errorToast(response.error);
      setEmployees([]);
      return;
    }
    const nonMappedEmployees = response?.data ?? response;
    setEmployees(
      Array.isArray(nonMappedEmployees)
        ? nonMappedEmployees.map(item => ({
            label: `${item.firstName} ${item.lastName}-${item.internalEmployeeId}`,
            value: item.employeeId
          }))
        : []
    );
  };

  const getMentors = async (employeeTermId, preSelectedMentor) => {
    const mentorsdata = await getApi(`${GET_ALL_MENTOR}/${id}?employeeTermGuid=${employeeTermId}`);
    setMentors(mentorsdata.map(item => ({ label: item.firstName + " " + item.lastName, value: item.employeeId })) || []);
    if (preSelectedMentor) return setMentors(...mentors, [preSelectedMentor]);
  };

  const getPerDiem = async item => {
    if (item) {
      const res = await getApi(`${GET_PROJECT_JOBTITLE}/${item}`);
      // Wage data moved to the nested prevailingWageRate object (two-table model). The job title's
      // straight-time prevailing wage/fringe seed the employee's rate + fringe defaults.
      const prevailingStraightTimeWage = parseFloat(res.data.prevailingWageRate?.straightTimeWage ?? 0);
      const perDiem = parseFloat(res.data.perDiem ?? 0);
      const prevailingStraightTimeFringe = parseFloat(res.data.prevailingWageRate?.straightTimeFringe ?? 0);
      setValue("perDiem", perDiem);
      setValue("fringe", prevailingStraightTimeFringe);
      setValue("rate", prevailingStraightTimeWage);
      setValue("actualHourlyRate", prevailingStraightTimeWage + prevailingStraightTimeFringe);
    }
  };

  const handleStatusToggle = async (params, newStatus) => {
    const statusText = newStatus ? "Active" : "Inactive";
    const employeeName = params.row.fullName || params.row.firstName || "this employee";

    setPendingToggle({
      employmentTermId: params.row.employmentTermId,
      newStatus: newStatus
    });

    setConfirmDialog({
      open: true,
      params,
      newStatus,
      employeeName,
      statusText
    });
  };

  const handleYesContinue = async () => {
    const { params, newStatus } = confirmDialog;

    try {
      const payload = {
        ...params.row,
        isActive: newStatus
      };

      const { error } = await postApi(UPDATE_PROJECT_PROJECTEMPLOYEE, payload);
      if (error) {
        errorToast(error);
        return;
      }

      setToggleStates(prev => ({
        ...prev,
        [params.row.employmentTermId]: newStatus
      }));

      getAllProjectEmployee();
      successToast(`Employee status updated to ${newStatus ? "Active" : "Inactive"}`);
    } catch (err) {
      errorToast("Failed to update employee status");
    } finally {
      setConfirmDialog({ open: false, params: null, newStatus: null, employeeName: "", statusText: "" });
      setPendingToggle(null);
    }
  };

  const handleNoCancel = () => {
    setPendingToggle(null);
    setConfirmDialog({ open: false, params: null, newStatus: null, employeeName: "", statusText: "" });
  };

  const defaultFormProps = { control, formValues: projectEmployeeData, handleReadValueClick };

  useEffect(() => {
    if (employeeData && employeeData.length > 0) {
      setToggleStates(prevToggleStates => {
        const newToggleStates = { ...prevToggleStates };
        employeeData.forEach(employee => {
          if (!(employee.employmentTermId in prevToggleStates)) {
            newToggleStates[employee.employmentTermId] = employee.isActive;
          }
        });
        return newToggleStates;
      });
    }
  }, [employeeData]);

  const renderBooleanCell = params => {
    const { value, row } = params;
    const { isUnionLabor, isUnionEmployee } = row;
    if (isUnionEmployee && !isUnionLabor) {
      return (
        <Box sx={{ color: "red", display: "flex", gap: 1 }}>
          {value ? "Yes" : "No"}
          <Tooltip title={"This employee is a union employee but labor classification is not a union labor"} placement="top">
            <HelpIcon color="primary" sx={{ fontSize: 16 }} />
          </Tooltip>
        </Box>
      );
    }
    return <Box>{value ? "Yes" : "No"}</Box>;
  };

  const columns = useColumn([
    {
      field: "fullName",
      headerName: "Full Name",
      renderCell: params => <NameCell params={params} onClick={onProjectEmployeeClick} />
    },
    {
      field: "employeeInternalId",
      headerName: "Employee Internal ID",
      width: 180
    },
    {
      field: "projectJobTitle",
      headerName: "Labor Classification Title",
      width: 200
    },
    {
      field: "isActive",
      headerName: "Active",
      width: 120,
      renderCell: params => (
        <>
          <ToggleCell
            params={params}
            toggleStates={toggleStates}
            employeeListPermission={employeeListPermission}
            handleStatusToggle={handleStatusToggle}
          />
          <Box>{params.row.isActive ? "Active" : "Inactive"}</Box>
        </>
      ),
      valueGetter: params => (params.row.isActive ? "Active" : "Inactive"),
      filterOperators: [getSelectFilterOperator(["Active", "Inactive"])]
    },
    {
      field: "isUnionLabor",
      headerName: "Union Labor Class",
      align: "center",
      renderCell: params => <Box>{params.row.isUnionLabor === true ? "Yes" : "No"}</Box>,
      filterOperators: [getSelectFilterOperator(["Yes", "No"])]
    },
    {
      field: "isUnionEmployee",
      headerName: "Union Employee",
      align: "center",
      renderCell: params => <Box>{params.row.isUnionEmployee === true ? "Yes" : "No"}</Box>,
      filterOperators: [getSelectFilterOperator(["Yes", "No"])]
    },
    {
      field: "isApprentice",
      headerName: "Apprentice",
      renderCell: params => <Box>{params.row.isApprentice === true ? "Yes" : "No"}</Box>,
      filterOperators: [getSelectFilterOperator(["Yes", "No"])]
    },
    {
      field: "isMentor",
      headerName: "Mentor",
      renderCell: params => <Box>{params.row.isMentor === true ? "Yes" : "No"}</Box>,
      filterOperators: [getSelectFilterOperator(["Yes", "No"])]
    },
    {
      field: "isJourneyMan",
      headerName: "Journeyman",
      renderCell: params => <Box>{params.row.isJourneyMan === true ? "Yes" : "No"}</Box>,
      filterOperators: [getSelectFilterOperator(["Yes", "No"])]
    }
  ]);

  const handleOpenDialogpopup = () => {
    setIsOpen(true);
  };

  const handleCloseDialogpopup = () => {
    setIsOpen(false);
  };

  const handleSubmitImport = async ({ validData }) => {
    try {
      // PSPD-2132 (PSPD-2129 epic): call the V2 endpoint. projectId moves into the URL path and
      // the payload envelope is { data: [...] } (was { projectId, excelData }). Rows are mapped
      // explicitly to the V2 ProjectEmployeeInputDto shape rather than relying on header camelCasing.
      const data = validData.map(mapProjectEmployee);
      const url = UPLOAD_PROJECT_EMPLOYEES.replace("{projectId}", id);
      const res = await postApi(url, { data });
      setShowProjectEmployeesConfirmModal(true);

      // PSPD-2133: the axios response interceptor (services/index.js) unwraps the response and
      // returns the raw string body for the V2 endpoint's Ok(string) conflict report. So on
      // success `res` IS that string; on failure postApi returns { error: <message> }.
      if (res?.error) return setApiResponse(res.error);
      setApiResponse(res);
      getAllProjectEmployee();
    } catch (err) {
      errorToast("Project Employees Excel Data Failed to Upload");
    }
  };

  const handleDownloadCSV = useCallback(async () => {
    try {
      const safePageSize = rowCount > 0 ? rowCount : 10000;
      const activeSort = sortModel?.[0] || { field: "fullName", sort: "asc" };
      const payload = {
        pageIndex: 1,
        pageSize: safePageSize,
        searchString,
        orderByAscending: activeSort.sort === "asc",
        orderCol: activeSort.field
      };

      const { data, error } = await postApi(GET_ALL_PROJECTEMPLOYEE, payload, {
        params: { projectId: id }
      });

      if (error) return errorToast("Download failed.");
      if (!data?.length) return errorToast("No data to export.");

      const filteredData = data.map(item => ({
        "Full Name": item.fullName || "",
        "Employee Internal ID": item.employeeInternalId || "",
        "Job Title": item.projectJobTitle || "",
        Status: item.isActive ? "Active" : "Inactive",
        Apprentice: item.isApprentice === true ? "Yes" : "No",
        Mentor: item.isMentor === true ? "Yes" : "No",
        Journeyman: item.isJourneyMan === true ? "Yes" : "No"
      }));

      const csv = convertToCSV(filteredData);
      downloadCSV(csv, "ProjectEmployees.csv");
    } catch (err) {
      console.error("CSV Export Error:", err);
      errorToast("An unexpected error occurred.");
    }
  }, [id, rowCount, searchString, sortModel]);

  const handleCloseProjectEmployeesModal = () => {
    setShowProjectEmployeesConfirmModal(false);
  };

  const handleDownload = () => {
    const filePath = "/static/Images/Excel/ProjectEmployeeImportTemplate.xlsx";
    window.open(filePath, "_blank");
  };

  useEffect(() => {
    getAllJobTitles();
    getAllEmployeeTermType();
  }, []);

  useEffect(() => {
    setValue("actualHourlyRate", (parseFloat(fringe || 0) + parseFloat(hourlyWageRate || 0)).toFixed(2));
  }, [fringe, hourlyWageRate]);

  useEffect(() => {
    if (!projectEmployeeData?.employmentTermId) getAllEmployees(isApprentice);
  }, [isApprentice, projectEmployeeData]);

  useEffect(() => {
    getAllProjectEmployee();
  }, [getAllProjectEmployee]);

  useEffect(() => {
    if (isApprentice) {
      setValue("isMentor", false);
    }
  }, [isApprentice]);

  useEffect(() => {
    if (projectJobTitleId && !projectEmployeeData?.employmentTermId) {
      getPerDiem(projectJobTitleId);
    }
  }, [projectJobTitleId]);

  return (
    <TabGridAndFormLayout
      showGrid={showGrid}
      title={"Employee"}
      backLabel="Go back to Employee List"
      backAction={() => resetForm()}
      beingEdited={isEditModeOn}
      onEdit={onEdit}
      onSave={handleSubmit(onSave)}
      onCancel={onCancel}
      hideEdit={!addEmployeePermission.canEdit}
      canView={employeeListPermission.canView}
      isSaving={isSaving}
    >
      {showGrid && (
        <ProGrid
          title="Employee"
          columns={columns}
          loading={status === STAUTES.LOADING}
          rows={employeeData}
          searchMode={!!searchString}
          searchModel={{ defaultValue: searchString, handleDebounce: setSearchString }}
          addButtonLabel="+ Add Employee"
          addButtonAction={addButtonAction}
          hideAddButton={!employeeListPermission.canAdd}
          hideExport={true}
          exportAction={handleDownloadCSV}
          additionButtons={
            employeeListPermission.canExport && (
              <ProButton variant="outlined" onClick={handleDownloadCSV}>
                Download as CSV
              </ProButton>
            )
          }
          options={{
            getRowId: row => row.employmentTermId,
            rowCount: rowCount,
            paginationMode: "server",
            paginationModel: { pageSize: pageSize, page: currentPage },
            onPaginationModelChange: setPagination,
            sortingMode: "server",
            onSortModelChange: setSortModel
          }}
          showImportExcel={employeeListPermission.canImport}
          showDownloadSampleExcel={employeeListPermission.canImport}
          importExcelAction={handleOpenDialogpopup}
          downloadSampleExcelAction={handleDownload}
          importExcelLabel={"Import Excel"}
          downloadSampleExcelLabel={"Project Employee Import Template"}
        />
      )}
      {!showGrid && (
        <>
          <ProToggleField
            {...defaultFormProps}
            {...formConfig.isApprentice}
            permission={addEmployeePermission.fieldPerObj[formConfig.isApprentice.perKey]}
          />
          <ProSelectField
            {...defaultFormProps}
            {...formConfig.projectJobTitleId}
            label={
              <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
                {formConfig.projectJobTitleId.label}
                {projectEmployeeData?.hasTimesheets && (
                  <Tooltip
                    title="A Timesheet has already been uploaded for this employee, so this labor classification cannot be changed. To use a different labor classification, add a separate employee profile for this project."
                    placement="top"
                  >
                    <HelpIcon color="primary" sx={{ fontSize: 16, cursor: "pointer", verticalAlign: "middle" }} />
                  </Tooltip>
                )}
              </Box>
            }
            placeholder={formConfig.projectJobTitleId.label}
            options={jobTitle}
            permission={{
              ...addEmployeePermission.fieldPerObj[formConfig.projectJobTitleId.perKey],
              canEdit: projectEmployeeData?.hasTimesheets ? false : addEmployeePermission.fieldPerObj[formConfig.projectJobTitleId.perKey]?.canEdit
            }}
          />
          {isAddMode ? (
            <ProSelectField
              {...defaultFormProps}
              {...formConfig.employeeId}
              options={employees}
              permission={addEmployeePermission.fieldPerObj[formConfig.employeeId.perKey]}
            />
          ) : (
            <ProSelectField
              {...defaultFormProps}
              {...formConfig.employeeId}
              options={employees}
              mode={"read"}
              permission={addEmployeePermission.fieldPerObj[formConfig.employeeId.perKey]}
            />
          )}
          <DateField {...defaultFormProps} {...formConfig.startDate} permission={addEmployeePermission.fieldPerObj[formConfig.startDate.perKey]} />
          <DateField
            {...defaultFormProps}
            {...formConfig.endDate}
            fieldProps={{ minDate: startDate }}
            permission={addEmployeePermission.fieldPerObj[formConfig.endDate.perKey]}
          />
          <ProSelectField
            {...defaultFormProps}
            {...formConfig.employmentTermTypeId}
            options={employeeTermType}
            permission={addEmployeePermission.fieldPerObj[formConfig.employmentTermTypeId.perKey]}
          />
          <ProTextInput {...defaultFormProps} {...formConfig.perDiem} permission={addEmployeePermission.fieldPerObj[formConfig.perDiem.perKey]} />
          <ProTextInput {...defaultFormProps} {...formConfig.rate} permission={addEmployeePermission.fieldPerObj[formConfig.rate.perKey]} />
          <ProTextInput {...defaultFormProps} {...formConfig.fringe} permission={addEmployeePermission.fieldPerObj[formConfig.fringe.perKey]} />
          <ProTextInput
            {...defaultFormProps}
            {...formConfig.actualHourlyRate}
            permission={addEmployeePermission.fieldPerObj[formConfig.actualHourlyRate.perKey]}
          />
          <ProCheckbox {...defaultFormProps} {...formConfig.isActive} permission={fieldTruePermission} />
          {isMentor === true && projectJobData?.IsRole === true && (
            <>
              <CheckBoxGroup {...defaultFormProps} {...formConfig.typicalshiftdays} options={weekdaysOptions} permission={fieldTruePermission} />
              <TimeField {...defaultFormProps} {...formConfig.shiftStartTime} permission={fieldTruePermission} />
              <TimeField {...defaultFormProps} {...formConfig.shiftEndTime} permission={fieldTruePermission} />
            </>
          )}
          {showLogGrid === true && employeeTermId !== "" ? (
            <Box sx={{ pt: 4, width: "100%" }}>
              <FormSectionWrapper collapsible defaultClose title={"Apprentice Mentor History"}>
                <ApprenticeMentorHistory actionData={projectEmployeeData} />
              </FormSectionWrapper>
            </Box>
          ) : (
            <></>
          )}
        </>
      )}
      <ReactSpreadsheetImport isOpen={isOpen} onClose={handleCloseDialogpopup} onSubmit={handleSubmitImport} fields={fields} />
      <DialogModal
        show={showProjectEmployeesConfirmModal}
        title="Message"
        message={` ${apiResponse}`}
        onConfirm={handleCloseProjectEmployeesModal}
        showCancel={false}
      />

      <Dialog
        open={confirmDialog.open}
        onClose={handleNoCancel}
        aria-labelledby="confirm-dialog-title"
        aria-describedby="confirm-dialog-description"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle id="confirm-dialog-title">Confirm Status Change</DialogTitle>
        <DialogContent>
          <DialogContentText id="confirm-dialog-description">
            Are you sure you want to change {confirmDialog.employeeName}'s status to {confirmDialog.statusText}?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ padding: "16px 24px" }}>
          <Button onClick={handleNoCancel} variant="outlined" color="primary" sx={{ minWidth: "120px" }}>
            No, Cancel
          </Button>
          <Button onClick={handleYesContinue} variant="contained" color="primary" autoFocus sx={{ minWidth: "120px" }}>
            Yes, Continue
          </Button>
        </DialogActions>
      </Dialog>
    </TabGridAndFormLayout>
  );
}
